import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

const STATUS = {
  BROUILLON: ['gray', 'Brouillon'],
  VALIDE: ['blue', 'Validée'],
  PUBLIE: ['green', 'Publiée'],
  CORRIGE: ['blue', 'Corrigée'],
  CLOTURE: ['gold', 'Clôturée'],
};

// Transitions proposées selon le statut courant.
const NEXT = {
  BROUILLON: [{ to: 'PUBLIE', label: 'Publier', primary: true }],
  VALIDE: [{ to: 'PUBLIE', label: 'Publier', primary: true }, { to: 'CLOTURE', label: 'Clôturer' }],
  PUBLIE: [
    { to: 'CORRIGE', label: 'Marquer corrigée' },
    { to: 'CLOTURE', label: 'Clôturer' },
    { to: 'BROUILLON', label: 'Repasser en brouillon' },
  ],
  CORRIGE: [{ to: 'CLOTURE', label: 'Clôturer' }, { to: 'PUBLIE', label: 'Republier' }],
  CLOTURE: [{ to: 'PUBLIE', label: 'Rouvrir', reason: true }],
};

const VALIDATORS = ['ADMIN_ECOLE', 'DIRECTEUR', 'CENSEUR', 'SUPER_ADMIN'];

export default function EvaluationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ev, setEv] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const canValidate = (user?.roles || []).some((r) => VALIDATORS.includes(r));

  const load = useCallback(() => {
    return Promise.all([api.get(`/evaluations/${id}`), api.get(`/evaluations/${id}/statistics`)])
      .then(([e, s]) => {
        setEv(e);
        setStats(s);
      })
      .catch((err) => setError(err.message));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  async function changeStatus(to, needsReason) {
    setError('');
    let reason;
    if (needsReason) {
      reason = window.prompt('Motif de réouverture (obligatoire) :');
      if (!reason) return;
    }
    setBusy(true);
    try {
      await api.patch(`/evaluations/${id}/status`, { status: to, reason });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  if (error && !ev) return <div className="form-error">{error}</div>;
  if (!ev) return <div className="loading">Chargement de l'évaluation…</div>;

  const [badge, label] = STATUS[ev.status] || ['gray', ev.status];
  const canEnter = ev.status !== 'CLOTURE';
  const results = [...(ev.results || [])].sort(
    (a, b) => (b.noteSur20 ?? -1) - (a.noteSur20 ?? -1),
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Link to="/evaluations" className="back-link">← Retour aux évaluations</Link>
        {canEnter && (
          <Link to={`/evaluations/${ev.id}/saisie`} className="btn btn-sm"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>
            Saisir les notes
          </Link>
        )}
      </div>

      <div className="detail-head">
        <h1>{ev.title}</h1>
        <span className={`badge ${badge}`}>{label}</span>
      </div>
      <div className="detail-meta">
        <div className="detail-meta-item"><span>Matière</span><b>{ev.subject?.name}</b></div>
        <div className="detail-meta-sep" />
        <div className="detail-meta-item"><span>Classe</span><b>{ev.class?.name}</b></div>
        <div className="detail-meta-sep" />
        <div className="detail-meta-item"><span>Période</span><b>{ev.period?.name}</b></div>
        <div className="detail-meta-sep" />
        <div className="detail-meta-item"><span>Barème</span><b className="mono">/{ev.baremeTotal}</b></div>
        <div className="detail-meta-sep" />
        <div className="detail-meta-item"><span>Coefficient</span><b className="mono">{ev.coefficient}</b></div>
      </div>

      {canValidate && (
        <div className="status-bar">
          <span className="status-bar-label">Statut · actions</span>
          <div className="status-bar-actions">
            {(NEXT[ev.status] || []).map((a) => (
              <button
                key={a.to}
                className={a.primary ? 'btn btn-sm' : 'btn-ghost btn-sm'}
                disabled={busy}
                onClick={() => changeStatus(a.to, a.reason)}
              >
                {a.label}
              </button>
            ))}
            {(NEXT[ev.status] || []).length === 0 && (
              <span style={{ color: 'var(--muted)', fontSize: '0.86rem' }}>Aucune action disponible.</span>
            )}
          </div>
        </div>
      )}

      {error && <div className="form-error">{error}</div>}

      {stats && (
        <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(6, 1fr)', marginBottom: 20 }}>
          <div className="kpi navy"><span>Moyenne /20</span><strong>{stats.moyenne ?? '—'}</strong></div>
          <div className="kpi"><span>Médiane</span><strong>{stats.mediane ?? '—'}</strong></div>
          <div className="kpi green"><span>Note max</span><strong>{stats.noteMax ?? '—'}</strong></div>
          <div className="kpi red"><span>Note min</span><strong>{stats.noteMin ?? '—'}</strong></div>
          <div className="kpi"><span>Écart-type</span><strong>{stats.ecartType ?? '—'}</strong></div>
          <div className={`kpi ${stats.tauxReussite >= 70 ? 'green' : stats.tauxReussite >= 50 ? 'gold' : stats.tauxReussite != null ? 'red' : ''}`}>
            <span>Taux de réussite</span>
            <strong>{stats.tauxReussite != null ? `${stats.tauxReussite}%` : '—'}</strong>
          </div>
        </div>
      )}

      <div className="panel">
        <div className="panel-head">
          <h2>Résultats des élèves</h2>
          <span style={{ color: 'var(--muted)', fontSize: '0.86rem' }}>
            {stats?.nbEvalues ?? 0} noté(s) · {stats?.nbAbsents ?? 0} absent(s)
          </span>
        </div>
        <div className="panel-body">
          {results.length === 0 ? (
            <div className="empty">Aucune note saisie. Clique sur « Saisir les notes ».</div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Rang</th>
                    <th>Matricule</th>
                    <th>Élève</th>
                    <th className="num">Note /20</th>
                    <th>Appréciation</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((r, i) => (
                    <tr key={r.id}>
                      <td className="mono">{r.absent ? '—' : i + 1}</td>
                      <td className="mono">{r.student?.matricule}</td>
                      <td>{r.student?.lastName} {r.student?.firstName}</td>
                      <td className="num">
                        {r.absent ? <span className="badge red">Absent</span> : <b>{r.noteSur20}</b>}
                      </td>
                      <td>{r.appreciation || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
